import React, { Suspense, useState } from 'react';
import type { ComponentType } from 'react';

import { PageLoading } from '../../core-components/theme/components/Screen/ScreenStates';
import { tabStorage } from '../../storage/deviceStorage';

const LoadingFallback = <PageLoading />;

const RELOAD_COOLDOWN_MS = 10_000;

const isChunkLoadError = (error: unknown): boolean => {
  const message = (error as { message?: unknown } | null)?.message;
  return (
    typeof message === 'string' &&
    /dynamically imported module|module script failed|importing a module|ChunkLoadError|Loading chunk .* failed|Failed to fetch/i.test(message)
  );
};

const lastReloadAt = (): number => Number(tabStorage.read('chunkReloadAt')) || 0;

const loadWithChunkRecovery = <M,>(load: () => Promise<M>): Promise<M> =>
  load().catch((error: unknown) => {
    const recentlyReloaded = Date.now() - lastReloadAt() < RELOAD_COOLDOWN_MS;
    if (isChunkLoadError(error) && !recentlyReloaded) {
      tabStorage.write('chunkReloadAt', String(Date.now()));
      window.location.reload();
      return new Promise<M>(() => undefined);
    }
    throw error;
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PageComponent = ComponentType<any>;

export interface LazyPage {
  (props: React.ComponentProps<PageComponent>): JSX.Element;
  preload: () => Promise<void>;
}

interface SharedLoad<M> {
  readonly get: () => Promise<M>;
  readonly value: () => M | null;
}

const sharedLoad = <M,>(importer: () => Promise<M>): SharedLoad<M> => {
  let value: M | null = null;
  let loading: Promise<M> | null = null;
  const get = (): Promise<M> => {
    loading ??= loadWithChunkRecovery(importer).then(
      (module) => {
        value = module;
        return module;
      },
      (error: unknown) => {
        loading = null;
        throw error;
      },
    );
    return loading;
  };
  return { get, value: () => value };
};

const lazyPage = (resolve: () => Promise<PageComponent>, ready: () => PageComponent | null, fallback: ChunkFallback): LazyPage => {
  const fresh = (): React.LazyExoticComponent<PageComponent> =>
    React.lazy(() =>
      resolve().then(
        (page) => ({ default: page }),
        (error: unknown) => {
          current = fresh();
          throw error;
        },
      ),
    );
  let current = fresh();

  const LazyLoaded = (props: React.ComponentProps<PageComponent>): JSX.Element => {
    const [Page] = useState(() => ready() ?? current);
    if (fallback === PARENT_FALLBACK) return <Page {...props} />;
    return (
      <Suspense fallback={fallback}>
        <Page {...props} />
      </Suspense>
    );
  };
  LazyLoaded.preload = (): Promise<void> => resolve().then(
    () => undefined,
    () => undefined,
  );
  return LazyLoaded;
};

function withLazyLoading(importFunc: () => Promise<{ default: PageComponent }>, fallback: React.ReactNode = LoadingFallback): LazyPage {
  const load = sharedLoad(importFunc);
  return lazyPage(
    () => load.get().then((module) => module.default),
    () => load.value()?.default ?? null,
    fallback,
  );
}

export const PARENT_FALLBACK: unique symbol = Symbol('withLazyLoading.parentFallback');

export type ChunkFallback = React.ReactNode | typeof PARENT_FALLBACK;

export type ChunkMember<M> = (pick: (module: M) => PageComponent, fallback?: ChunkFallback) => LazyPage;

export const lazyChunk = <M,>(importer: () => Promise<M>): ChunkMember<M> => {
  const load = sharedLoad(importer);
  return (pick, fallback = LoadingFallback) => {
    const ready = (): PageComponent | null => {
      const chunk = load.value();
      return chunk === null ? null : pick(chunk);
    };
    return lazyPage(() => load.get().then(pick), ready, fallback);
  };
};

export const carryPreload = <C extends object>(component: C, page: Pick<LazyPage, 'preload'>): C & Pick<LazyPage, 'preload'> =>
  Object.assign(component, { preload: page.preload });

export default withLazyLoading;
